import React, { useState, useEffect, useCallback } from 'react';
import { FileCheck, Plus, Edit2, Trash2, ListChecks, Link2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { apqpService } from '../../services/api';

const STATUSES = [
  'Non richiesto', 'Da preparare', 'In preparazione', 'Completato',
  'Inviato al cliente', 'Approvato', 'Da revisionare',
];

const STATUS_COLOR = {
  'Non richiesto':      'bg-slate-100 text-slate-600 border-slate-300',
  'Da preparare':       'bg-blue-100 text-blue-800 border-blue-300',
  'In preparazione':    'bg-sky-100 text-sky-800 border-sky-300',
  'Completato':         'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Inviato al cliente': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Approvato':          'bg-teal-100 text-teal-800 border-teal-300',
  'Da revisionare':     'bg-amber-100 text-amber-800 border-amber-300',
};

const defaultForm = () => ({
  name: '', category: '', status: 'Da preparare', linked_task_id: '', notes: '',
});

export default function ApqpDocumentsTab({ projectId, tasks = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState({ open: false, editing: null, form: defaultForm() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apqpService.listDocuments(projectId);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento documenti');
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setDlg({ open: true, editing: null, form: defaultForm() });
  const openEdit = (d) => setDlg({
    open: true, editing: d.id,
    form: {
      name: d.name || '', category: d.category || '', status: d.status || 'Da preparare',
      linked_task_id: d.linked_task_id || '', notes: d.notes || '',
    },
  });

  const save = async () => {
    const { editing, form } = dlg;
    if (!form.name?.trim()) { toast.error('Nome documento obbligatorio'); return; }
    const payload = { ...form };
    if (payload.linked_task_id === '') payload.linked_task_id = null;
    if (payload.category === '') payload.category = null;
    if (payload.notes === '') payload.notes = null;
    try {
      if (editing) await apqpService.updateDocument(editing, payload);
      else await apqpService.createDocument(projectId, payload);
      toast.success(editing ? 'Documento aggiornato' : 'Documento aggiunto');
      setDlg(d => ({ ...d, open: false }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare il documento "${d.name}"?`)) return;
    try {
      await apqpService.deleteDocument(d.id);
      toast.success('Documento eliminato');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const populateStandard = async () => {
    try {
      const r = await apqpService.populateStandardDocuments(projectId);
      toast.success(`Aggiunti ${r.data.inserted} documenti standard`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore popolamento');
    }
  };

  const quickChangeStatus = async (d, newStatus) => {
    try {
      await apqpService.updateDocument(d.id, {
        name: d.name, category: d.category, status: newStatus,
        linked_task_id: d.linked_task_id, notes: d.notes,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore modifica stato');
    }
  };

  const stats = STATUSES.map(s => ({ status: s, count: items.filter(x => x.status === s).length }));

  return (
    <div className="space-y-3" data-testid="apqp-documents-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-slate-800">Documentazione PPAP</h3>
          <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px]">{items.length}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={populateStandard} data-testid="populate-standard-btn">
            <ListChecks className="w-4 h-4 mr-1" /> Aggiungi lista standard
          </Button>
          <Button size="sm" onClick={openNew} data-testid="new-document-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo documento
          </Button>
        </div>
      </div>

      {/* Stats badges */}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {stats.filter(s => s.count > 0).map(s => (
            <Badge key={s.status} className={`${STATUS_COLOR[s.status]} border text-[10px]`}>
              {s.status}: <span className="font-bold ml-1">{s.count}</span>
            </Badge>
          ))}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left">
                  <th className="px-2 py-2 font-medium">Documento</th>
                  <th className="px-2 py-2 font-medium">Categoria</th>
                  <th className="px-2 py-2 font-medium">Stato</th>
                  <th className="px-2 py-2 font-medium">Attività collegata</th>
                  <th className="px-2 py-2 font-medium">Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && items.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-6">Caricamento…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-slate-400 py-8" data-testid="documents-empty">
                      Nessun documento. Usa &quot;Aggiungi lista standard&quot; per popolare la checklist PPAP tipica.
                    </td>
                  </tr>
                )}
                {items.map(d => {
                  const linkedTask = tasks.find(t => t.id === d.linked_task_id);
                  return (
                    <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                      <td className="px-2 py-1.5 font-medium">{d.name}</td>
                      <td className="px-2 py-1.5 text-slate-500">{d.category || '—'}</td>
                      <td className="px-2 py-1.5">
                        <Select value={d.status} onValueChange={(v) => quickChangeStatus(d, v)}>
                          <SelectTrigger className={`h-7 text-xs border ${STATUS_COLOR[d.status]}`} data-testid={`doc-status-${d.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">
                        {linkedTask ? (
                          <span className="inline-flex items-center gap-1 text-blue-700">
                            <Link2 className="w-3 h-3" /> {linkedTask.name}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600 max-w-[200px] truncate" title={d.notes || ''}>{d.notes || '—'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`edit-doc-${d.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(d)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dlg.open} onOpenChange={(o) => !o && setDlg(d => ({ ...d, open: false }))}>
        <DialogContent className="max-w-xl" data-testid="apqp-doc-dialog">
          <DialogHeader>
            <DialogTitle>{dlg.editing ? 'Modifica documento' : 'Nuovo documento'}</DialogTitle>
            <DialogDescription>Definisci nome, categoria, stato attuale ed eventuale collegamento a un&apos;attività del Gantt.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nome documento *</Label>
              <Input value={dlg.form.name} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, name: e.target.value } }))} data-testid="doc-name-input" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Input value={dlg.form.category} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, category: e.target.value } }))} placeholder="es. Documentazione tecnica" />
            </div>
            <div>
              <Label>Stato</Label>
              <Select value={dlg.form.status} onValueChange={(v) => setDlg(d => ({ ...d, form: { ...d.form, status: v } }))}>
                <SelectTrigger data-testid="doc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Attività Gantt collegata (opzionale)</Label>
              <Select value={dlg.form.linked_task_id || 'none'} onValueChange={(v) => setDlg(d => ({ ...d, form: { ...d.form, linked_task_id: v === 'none' ? '' : v } }))}>
                <SelectTrigger data-testid="doc-linked-task-select"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuna —</SelectItem>
                  {tasks.filter(t => t.task_type !== 'phase').map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Note</Label>
              <Input value={dlg.form.notes} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, notes: e.target.value } }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(d => ({ ...d, open: false }))}>Annulla</Button>
            <Button onClick={save} data-testid="save-doc-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
